<!-- TreeView.vue -->
<template>
    <div v-if="treeNodes">
        <div class="flex items-center gap-2 py-2">
            <div v-if="allowSearch" class="flex items-center gap-2 py-2 w-1/2">
                <input
                    v-model="rawSearch"
                    type="text"
                    :placeholder="`Search: ${treeLevels.map((l) => l.schemaLevel).join(', ')}`"
                    class="block w-full appearance-none focus:outline-hidden focus:border-gray-300 rounded-md border px-2 py-1 placeholder-gray-400 shadow-sm sm:text-sm" />
            </div>
            <TreeCheckbox
                v-if="visible.length && !searchTerm"
                :checked="selectAll"
                @change="
                    () => {
                        selectAll = !selectAll;
                        selectAllNodes();
                    }
                " />
            <label v-if="visible.length && !searchTerm" class="text-sm">Select all</label>
            <!-- <button v-if="editMode && !disableCreateAndEdit" class="btn-add" @click="enableAllEditing">Edit all</button> -->
            <!-- <button v-if="editMode && !disableCreateAndEdit && anyEditing" class="btn-add" @click="closeAllEditing">Close All</button> -->
        </div>

        <div class="flex items-center gap-2 p-2 has-parent-border" :style="{ background }">
            <div v-if="visible.length" class="text-sm font-medium text-gray-700">
                {{ treeNodes.length }} {{ treeLevels[0].schemaLevel }}{{ treeNodes.length !== 1 ? 's' : '' }}
            </div>
            <button
                v-if="editMode && !disableCreateAndEdit"
                class="btn-add"
                @click.prevent="addNode(treeNodes, treeLevels[0].schemaLevel, treeLevels[0].schemaObject)">
                <PlusIcon class="h-4 w-4 mr-1" /> Add {{ treeLevels[0].schemaLevel }}
            </button>
            <button v-if="editMode && !disableCreateAndEdit" class="btn-add" @click="openFileUpload">
                <ArrowUpOnSquareStackIcon class="h-4 w-4 mr-1" /> Import .CSV
            </button>
            <input ref="fileInput" type="file" accept=".csv" class="hidden" @change="loadCsvFile" />

            <button v-if="editMode && !disableCreateAndEdit" class="btn-info" @click="downloadCsvTemplate(treeLevels[0].schemaLevel)">
                <ArrowDownTrayIcon class="h-4 w-4 mr-1" /> .CSV Template
            </button>
        </div>

        <div class="max-h-[70vh]" v-if="visible.length">
            <RecycleScroller
                :items="visible"
                key-field="id"
                variable
                size-field="size"
                :default-size="MAX_ROW_HEIGHT"
                class="max-h-[40vh] overflow-y-auto">
                <template #default="{ item }">
                    <div :style="{ paddingLeft: `${item.depth * indent}rem` }">
                        <TreeNode
                            v-memo="[item.id, item.node.name, item.isNodeChecked, item.isError, item.isEditing, collapsedMap[item.id]]"
                            :node="item.node"
                            :parent="item.parent"
                            :level="item.depth"
                            :collapsed="collapsedMap[item.id]"
                            :toggle-collapse="toggleCollapsed"
                            :tree-levels="treeLevels"
                            @select-toggled="
                                () => {
                                    propagateSelection(treeNodes);
                                }
                            "
                            :readonly="readonly"
                            :display-only-selected="displayOnlySelected"
                            :edit-mode="editMode"
                            :disable-create-and-edit="disableCreateAndEdit"
                            :background="background"
                            :custom-width="customWidth"
                            @duplicate="canSaveDuplicate" />
                    </div>
                </template>
            </RecycleScroller>
        </div>
        <div v-else class="text-center text-gray-500 py-4">
            <p>No {{ treeLevels[0].schemaLevel }}s found</p>
        </div>
    </div>
</template>

<script>
import { reactive, toRefs, ref, onMounted, watch, computed, provide, shallowReactive } from 'vue';
import { PlusIcon, ArrowUpOnSquareStackIcon, ArrowDownTrayIcon } from '@heroicons/vue/24/outline';
import TreeCheckbox from './TreeCheckbox.vue';
import TreeNode from './TreeNode.vue';
import Papa from 'papaparse';
import _ from 'lodash';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
    components: { PlusIcon, ArrowUpOnSquareStackIcon, TreeCheckbox, TreeNode, RecycleScroller, ArrowDownTrayIcon },
    props: {
        treeNodes: { type: Array, required: true },
        treeLevels: { type: Array, required: true },
        readonly: { type: Boolean, default: false },
        displayOnlySelected: { type: Boolean, default: false },
        editMode: { type: Boolean, default: false },
        disableCreateAndEdit: { type: Boolean, default: false },
        background: { type: String, default: 'white' },
        addDefaultEmptyNode: { type: Boolean, default: false },
        customWidth: { type: String, default: '95%' },
        allowSearch: { type: Boolean, default: false }
    },
    emits: ['can-save-duplicate'],
    setup(props, { emit }) {
        const state = reactive({ selectAll: false });
        const fileInput = ref(null);
        const rawSearch = ref('');
        const searchTerm = ref('');
        const collapsedMap = shallowReactive({});
        const sizeMap = shallowReactive({});
        const editingMap = shallowReactive({});

        watch(
            rawSearch,
            _.debounce((val) => {
                searchTerm.value = val;
            }, 300)
        );

        const indent = 2; // rem per level

        const ROW_HEIGHT = 44; // base row height in px
        const TOOLBAR_HEIGHT = 41; // height of the per-node toolbar in px
        const MAX_ROW_HEIGHT = ROW_HEIGHT + TOOLBAR_HEIGHT;

        function downloadCsvTemplate(schemaLevel) {
            // Build five example values, one per line
            const examples = Array.from({ length: 5 }, (_, i) => `${schemaLevel}-${i + 1}`);
            // Join them with newlines (CRLF) and add a final newline
            const csvContent = examples.join('\r\n') + '\r\n';

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${schemaLevel.toLowerCase()}-template.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }

        function clearAllErrors(nodes) {
            for (const n of nodes) {
                if (n.isError != null) delete n.isError;
                if (n.children) clearAllErrors(n.children);
            }
        }

        function computeSize(node) {
            const idx = props.treeLevels.findIndex((l) => l.schemaLevel === node.schemaLevel);
            const hasNext = !!props.treeLevels[idx + 1];
            let size = ROW_HEIGHT;

            if (props.editMode) {
                if (hasNext && !collapsedMap[node.id]) {
                    size = ROW_HEIGHT + (props.disableCreateAndEdit ? TOOLBAR_HEIGHT - 12 : TOOLBAR_HEIGHT);

                    if (!editingMap[node.id] && !props.disableCreateAndEdit) {
                        // if editing, expand the size
                        size -= 6;
                    }
                } else {
                    size = ROW_HEIGHT - (props.disableCreateAndEdit ? 5 : 0);

                    if (!editingMap[node.id] && !props.disableCreateAndEdit) {
                        // if not editing, shrink the size
                        size -= 5;
                    }
                }
            } else {
                if (hasNext && !collapsedMap[node.id]) {
                    size = ROW_HEIGHT + TOOLBAR_HEIGHT - 12;
                } else {
                    size = ROW_HEIGHT - 5;
                }
            }

            return size;
        }

        const visible = computed(() => {
            const list = [];
            const term = props.allowSearch && searchTerm.value.trim().toLowerCase();

            function matchesAnywhere(node) {
                if (!term) return true;
                if (node.name.toLowerCase().includes(term)) return true;
                return node.children?.some(matchesAnywhere);
            }

            function walk(nodes, depth = 0) {
                for (const node of nodes) {
                    if (!matchesAnywhere(node)) continue;

                    // ensure id
                    if (!node.id) {
                        Object.defineProperty(node, 'id', {
                            value: crypto.randomUUID(),
                            writable: false,
                            enumerable: false
                        });
                        // seed sizeMap for brand-new nodes
                        sizeMap[node.id] = computeSize(node);
                    }

                    // fetch the cached size, or compute+cache if missing
                    let size = sizeMap[node.id];
                    if (size == null) {
                        size = computeSize(node);
                        sizeMap[node.id] = size;
                    }

                    list.push({ id: node.id, node, depth, parent: nodes, size });

                    if (node.children?.length && !collapsedMap[node.id]) {
                        walk(node.children, depth + 1);
                    }
                }
            }

            walk(props.treeNodes);
            return list;
        });

        function checkRootErrors() {
            if (!props.editMode || props.disableCreateAndEdit) return;
            // count root‐level names
            const counts = props.treeNodes
                .map((n) => n.name.trim())
                .reduce((acc, name) => {
                    acc[name] = (acc[name] || 0) + 1;
                    return acc;
                }, {});

            // set error flag on each node
            props.treeNodes.forEach((n) => {
                const name = n.name.trim();
                const isEmpty = name === '';
                const containsWild = name.includes('*');
                const isDuplicate = counts[name] > 1;

                n.isError = isEmpty || containsWild || isDuplicate;
            });
        }

        function toggleCollapsed(id) {
            collapsedMap[id] = !collapsedMap[id];
        }

        const checkDuplicates = (nodes) => {
            const names = nodes.map((n) => n.name.trim());

            if (new Set(names).size !== names.length) return true;

            return nodes.some((n) => n.children && checkDuplicates(n.children));
        };

        const checkEmpty = (nodes) => {
            for (const n of nodes) {
                if (!n.name.trim()) return true;
                if (n.children && checkEmpty(n.children)) return true;
            }
            return false;
        };
        const canSaveDuplicate = () => {
            const dup = checkDuplicates(props.treeNodes);
            const empty = checkEmpty(props.treeNodes);

            emit('can-save-duplicate', !dup && !empty);
        };

        function addNode(nodes, schemaLevel, schemaObject, customName = null, fromCsv = false) {
            const placeholder = customName ?? `Enter ${schemaLevel} name`;
            const entry = {
                name: fromCsv ? customName : '',
                isSelected: true,
                collapsed: false,
                schemaLevel,
                schemaObject,
                children: [],
                placeholder
            };
            Object.defineProperty(entry, 'id', {
                value: crypto.randomUUID(),
                writable: false,
                enumerable: false
            });
            nodes.push(entry);
            collapsedMap[entry.id] = false;
            sizeMap[entry.id] = computeSize(entry);
            editingMap[entry.id] = true;

            canSaveDuplicate();
        }

        function removeNode(parentArr, node) {
            const idx = parentArr.indexOf(node);
            if (idx !== -1) {
                parentArr.splice(idx, 1);

                canSaveDuplicate();
            }
        }

        function selectAllNodes() {
            visible.value.forEach(({ node }) => {
                node.isSelected = state.selectAll;
            });
        }

        function enableAllEditing() {
            // first ensure every node has an entry
            visible.value.forEach(({ node }) => {
                if (!(node.id in editingMap)) editingMap[node.id] = true;
                else editingMap[node.id] = true;
            });
        }

        // function closeAllEditing() {
        //     visible.value.forEach(({ node, parent, id }) => {
        //         const name = (node.name || '').trim();
        //         const isEmpty = name === '';
        //         const containsWild = name.includes('*');
        //         const isDuplicate = parent.some((o) => o !== node && (o.name || '').trim() === name);
        //         if (!isEmpty && !containsWild && !isDuplicate) {
        //             editingMap[id] = false;
        //         }
        //     });

        //     console.log('editingMap', editingMap);
        // }

        // const selectAllNodes = () => {
        //     const recurse = (nodes, sel) => {
        //         for (const n of nodes) {
        //             n.isSelected = sel;
        //             if (n.children) recurse(n.children, sel);
        //         }
        //     };
        //     recurse(props.treeNodes, state.selectAll);
        // };
        const allSelected = (nodes) => nodes.every((n) => n.isSelected && (!n.children || allSelected(n.children)));

        function propagateSelection(nodes) {
            for (const n of nodes) {
                if (n.children?.length) {
                    // first fix up grandchildren
                    propagateSelection(n.children);
                    // then enforce parent = any(child.isSelected)
                    n.isSelected = n.children.some((c) => c.isSelected);
                }
            }
        }

        watch(
            () => props.treeNodes,
            (newTree) => {
                state.selectAll = allSelected(newTree);
                checkRootErrors();
            },
            { deep: true }
        );

        watch(
            () => ({ ...editingMap, ...collapsedMap, editMode: props.editMode }),
            () => {
                // only need to update sizes for nodes currently in view
                visible.value.forEach(({ node }) => {
                    sizeMap[node.id] = computeSize(node);
                });
            },
            { deep: true }
        );

        const openFileUpload = () => {
            fileInput.value.value = '';
            fileInput.value.click();
        };
        const loadCsvFile = (e) => {
            const file = e.target.files[0];
            if (!file?.name.toLowerCase().endsWith('.csv')) {
                alert('Please upload a valid CSV file.');
                return;
            }

            const batchSize = 1000;
            let buffer = [];

            function insertBatch(names) {
                const entries = names.map((name) => {
                    const entry = {
                        name,
                        isSelected: true,
                        collapsed: false,
                        schemaLevel: props.treeLevels[0].schemaLevel,
                        schemaObject: props.treeLevels[0].schemaObject,
                        children: [],
                        placeholder: name
                    };
                    Object.defineProperty(entry, 'id', {
                        value: crypto.randomUUID(),
                        writable: false,
                        enumerable: false
                    });
                    // make sure our collapsedMap stays in sync
                    collapsedMap[entry.id] = false;
                    sizeMap[entry.id] = computeSize(entry);

                    return entry;
                });
                props.treeNodes.splice(props.treeNodes.length, 0, ...entries);
            }

            Papa.parse(file, {
                worker: true,
                skipEmptyLines: true,
                step({ data }) {
                    const name = data[0]?.trim();
                    if (name) buffer.push(name);
                    if (buffer.length >= batchSize) {
                        insertBatch(buffer);
                        buffer = [];
                    }
                },
                complete() {
                    if (buffer.length) insertBatch(buffer);

                    clearAllErrors(props.treeNodes);
                    canSaveDuplicate();
                }
            });
        };

        onMounted(() => {
            props.treeNodes.forEach((n, i) => {
                if (!n.id) {
                    Object.defineProperty(n, 'id', {
                        value: crypto.randomUUID(),
                        writable: false,
                        enumerable: false
                    });
                }

                collapsedMap[n.id] = i !== 0;
                sizeMap[n.id] = computeSize(n);
            });

            if (props.addDefaultEmptyNode) {
                addNode(props.treeNodes, props.treeLevels[0].schemaLevel, props.treeLevels[0].schemaObject);
            }
            state.selectAll = allSelected(props.treeNodes);
        });

        provide(
            'visibleIds',
            computed(() => new Set(visible.value.map((item) => item.id)))
        );

        provide('sizeMap', sizeMap);
        provide('computeSize', computeSize);
        provide('editingMap', editingMap);

        // const anyEditing = computed(() => Object.values(editingMap).some((val) => val));

        return {
            ...toRefs(state),
            fileInput,
            visible,
            indent,
            addNode,
            removeNode,
            downloadCsvTemplate,
            searchTerm,
            editingMap,
            rawSearch,
            propagateSelection,
            selectAllNodes,
            canSaveDuplicate,
            collapsedMap,
            openFileUpload,
            loadCsvFile,
            toggleCollapsed,
            enableAllEditing,
            // closeAllEditing,
            // anyEditing,
            MAX_ROW_HEIGHT,
            sizeMap
        };
    }
};
</script>

<style>
@reference '../style.css';
.has-parent-border {
    border-bottom: 1px solid #c0c0c0;
    width: 100%;
}
.btn-add {
    @apply flex items-center px-2 py-1 border border-green-500 text-green-500 rounded-sm text-xs hover:bg-green-400 hover:text-white;
}

.btn-info {
    @apply flex items-center px-2 py-1 border border-blue-500 text-blue-500 rounded-sm text-xs hover:bg-blue-400 hover:text-white;
}

:deep(.vue-recycle-scroller__item-wrapper),
:deep(.vue-recycle-scroller__item-view) {
    overflow: visible !important;
}
</style>
